import { useContext, useEffect, useState } from "react";
import { Button, ButtonGroup, Table } from "reactstrap";
import moment from "moment";
import Select from "react-select";
import { Link } from "react-router-dom";
import EnterpriseService from "../../common/services/enterprise-service";
import UserDefinedUnits from "../../common/utils/UserDefinedUnits";
import reactSelectCustomStyles from "../../common/plugins/reactSelectStyles";
import { EnterpriseContext } from "./EnterpriseContext";
import PeriodSelector from "./PeriodSelector";
import OrderByButtons from "../ui/OrderByButtons";
import ReactSelectUserOption from "../ui/ReactSelectUserOption";
import Loader from "../ui/Loader";
require("linqjs");

export default function VehicleStats({ container, garage_id = null }) {
  const es = new EnterpriseService();

  const { globalContext } = useContext(EnterpriseContext);
  const company = globalContext.company;
  const member = globalContext.member;

  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [members, setMembers] = useState([]);
  const [membersIsLoading, setMembersIsLoading] = useState(false);

  const [period, setPeriod] = useState("monthly");
  const [currentPeriod, setCurrentPeriod] = useState({
    month: moment.utc().month(),
    year: moment.utc().year(),
  });

  const [gameId, setGameId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [sortingField, setSortingField] = useState("total_distance_driven_km");
  const [sortingDirection, setSortingDirection] = useState("desc");

  useEffect(() => {
    searchMembers();
  }, []);

  useEffect(() => {
    getStats();
  }, [period, currentPeriod.month, currentPeriod.year, gameId, userId]);

  const searchMembers = async () => {
    setMembersIsLoading(true);
    const result = await es.getMembers(company, { perPage: 9999 });
    setMembers(result.data);
    setMembersIsLoading(false);
  };

  const getStats = async () => {
    setLoading(true);

    const filters = {
      period,
      month: currentPeriod.month + 1,
      year: currentPeriod.year,
    };

    if (garage_id) filters.garage_id = garage_id;
    if (gameId != -1) filters.game_id = gameId;
    if (userId) filters.user_id = userId;

    const result = await es.getAllVehicleStats(company.id, filters);
    setVehicles(result || []);
    setLoading(false);
  };

  const changeSortingField = ({ field, direction }) => {
    setSortingField(field);
    setSortingDirection(direction);
  };

  const selectUserId = (option) => {
    setUserId(option == null ? null : option.id);
  };

  const getSortedVehicles = () => {
    return sortingDirection == "asc"
      ? vehicles.orderBy((m) => m[sortingField])
      : vehicles.orderByDescending((m) => m[sortingField]);
  };

  return (
    <>
      <div className="d-flex align-items-start justify-content-between flex-column flex-md-row mb-3 members-toolbar">
        <PeriodSelector
          enableDaily={false}
          enableWeekly={false}
          container={container}
          onChangeRange={setPeriod}
          onChangePeriod={setCurrentPeriod}
          state={{ range: period, currentPeriod: currentPeriod }}
        ></PeriodSelector>
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 mt-3 mt-md-0">
          <ButtonGroup>
            <Button
              color={gameId == null ? "primary" : "secondary"}
              onClick={() => setGameId(null)}
            >
              {container.localeManager.strings.all}
            </Button>
            <Button
              color={gameId == 1 ? "primary" : "secondary"}
              onClick={() => setGameId(1)}
            >
              ETS2
            </Button>
            <Button
              color={gameId == 2 ? "primary" : "secondary"}
              onClick={() => setGameId(2)}
            >
              ATS
            </Button>
          </ButtonGroup>
          <div style={{ minWidth: 220 }}>
            <Select
              isClearable
              placeholder={container.localeManager.strings.driver}
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? container.localeManager.strings.startTyping
                  : container.localeManager.strings.noResults
              }
              value={members.find((m) => m.id === userId) || null}
              onChange={selectUserId}
              getOptionValue={(m) => m.id}
              getOptionLabel={(m) => m.name}
              styles={reactSelectCustomStyles}
              options={members}
              isLoading={membersIsLoading}
              components={{
                Option: ReactSelectUserOption,
              }}
            ></Select>
          </div>
        </div>
      </div>
      {loading && <Loader></Loader>}
      {!loading && vehicles.length === 0 && (
        <div className="text-muted fst-italic text-center py-3">
          {container.localeManager.strings.noResults}
        </div>
      )}
      {!loading && vehicles.length > 0 && (
        <Table
          responsive
          striped
          size="sm"
          className="table-row-dashed table-row-gray-300 align-middle gs-0"
          hover
        >
          <thead>
            <tr className="fw-bolder text-gray-700 bg-light">
              <th className="ps-4">#</th>
              <th className="ps-4">
                {container.localeManager.strings.fleetNumber}
              </th>
              <th>{container.localeManager.strings.model}</th>
              {!garage_id && <th>{container.localeManager.strings.garage}</th>}
              <th className="text-end">
                {container.localeManager.strings.distance}
                <OrderByButtons
                  container={container}
                  currentSortingDirection={sortingDirection}
                  currentSortingField={sortingField}
                  field="total_distance_driven_km"
                  onClick={changeSortingField}
                ></OrderByButtons>
              </th>
              <th className="text-end">
                {container.localeManager.strings.revenue}
                <OrderByButtons
                  container={container}
                  currentSortingDirection={sortingDirection}
                  currentSortingField={sortingField}
                  field="total_revenue"
                  onClick={changeSortingField}
                ></OrderByButtons>
              </th>
              <th className="text-end">
                {container.localeManager.strings.fuel_used}
                <OrderByButtons
                  container={container}
                  currentSortingDirection={sortingDirection}
                  currentSortingField={sortingField}
                  field="total_fuel_used_l"
                  onClick={changeSortingField}
                ></OrderByButtons>
              </th>
              <th className="text-end pe-3">
                {container.localeManager.strings.damage_cost}
                <OrderByButtons
                  container={container}
                  currentSortingDirection={sortingDirection}
                  currentSortingField={sortingField}
                  field="total_damage_cost"
                  onClick={changeSortingField}
                ></OrderByButtons>
              </th>
            </tr>
          </thead>
          <tbody>
            {getSortedVehicles().map((m) => (
              <tr key={m.id}>
                <td className="ps-4">
                  <Link to={`/vehicle/${m.id}`}>{m.id}</Link>
                </td>
                <td className="ps-4">
                  <Link to={`/vehicle/${m.id}`}>#{m.fleet_number ?? m.id}</Link>
                </td>
                <td>
                  <div className="d-flex align-items-center">
                    {m.model?.photo_url && (
                      <img
                        src={m.model.photo_url}
                        className="avatar rounded h-30px me-3"
                      />
                    )}
                    <span>{m.model?.full_name || m.model?.name}</span>
                  </div>
                </td>
                {!garage_id && <td>{m.garage?.city?.name || "-"}</td>}
                <td className="text-end">
                  {UserDefinedUnits.convertDistance(
                    member,
                    null,
                    m.total_distance_driven_km,
                  )}
                </td>
                <td className="text-end">
                  {container.localeManager
                    .numeral(m.total_revenue)
                    .format("0,0")}{" "}
                  {company.currency}
                </td>
                <td className="text-end">
                  {UserDefinedUnits.convertVolume(
                    member,
                    null,
                    m.total_fuel_used_l,
                  )}
                </td>
                <td className="text-end pe-3">
                  {container.localeManager
                    .numeral(m.total_damage_cost)
                    .format("0,0")}{" "}
                  {company.currency}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}
